"use client";

/**
 * Switch — 켜짐/꺼짐 토글 스위치 (Radix Switch 기반)
 *
 * 두 개의 값 중 하나를 고르는 상태(예: 활성/비활성)를 select 대신 한눈에 보여줄 때 쓴다.
 * AdminFormField type="switch" 의 기반 프리미티브. 직접 조합해서 쓸 수도 있다.
 * size 로 크기를 조절할 수 있다 (기본 md) — 옆에 놓이는 input/select 와 톤을 맞추고 싶을 때 lg 이상 사용.
 * 테두리 없이 트랙 좌우로 2px 여유를 두고 썸이 움직인다 (antd Switch 스타일).
 *
 * @example
 * <Switch checked={enabled} onCheckedChange={setEnabled} />
 * <Switch size="xl" checked={enabled} onCheckedChange={setEnabled} />
 */
import { type ComponentProps } from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { cn } from "../../lib/utils";

export type SwitchSize = "sm" | "md" | "lg" | "xl" | "xxl";

const TRACK_SIZE: Record<SwitchSize, string> = {
  sm: "h-4 w-7",
  md: "h-[1.15rem] w-8",
  lg: "h-6 w-11",
  xl: "h-7 w-14",
  xxl: "h-8 w-16",
};

const THUMB_SIZE: Record<SwitchSize, string> = {
  sm: "size-3",
  md: "size-4",
  lg: "size-5",
  xl: "size-6",
  xxl: "size-7",
};

// 이동 거리(트랙 너비 - 좌우 여유 2px씩 - 썸 너비)를 트랙/썸 조합마다 정확히 맞춘 값.
// 트랙에 px-[2px] 여유를 둬서 antd Switch 처럼 썸이 양 끝에 완전히 붙지 않고 살짝 여백을 남긴다.
// sm: 28-4-12=12 / md: 32-4-16=12 / lg: 44-4-20=20 / xl: 56-4-24=28 / xxl: 64-4-28=32 (모두 4px 배수)
const THUMB_TRANSLATE: Record<SwitchSize, string> = {
  sm: "data-[state=checked]:translate-x-3",
  md: "data-[state=checked]:translate-x-3",
  lg: "data-[state=checked]:translate-x-5",
  xl: "data-[state=checked]:translate-x-7",
  xxl: "data-[state=checked]:translate-x-8",
};

export interface SwitchProps extends ComponentProps<typeof SwitchPrimitive.Root> {
  /** 크기 (기본 md) */
  size?: SwitchSize;
}

function Switch({ className, size = "md", ...props }: SwitchProps) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        "peer inline-flex shrink-0 items-center rounded-full px-[2px] shadow-xs outline-none transition-all focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
        TRACK_SIZE[size],
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          "pointer-events-none block rounded-full bg-background shadow-md ring-0 transition-transform data-[state=unchecked]:translate-x-0",
          THUMB_SIZE[size],
          THUMB_TRANSLATE[size]
        )}
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
